/**
 * Utility functions for date and time conversion and formatting
 */

import type { InstallationDto } from "@sitescene/types";
import type { DateValue } from "react-aria-components";

import {
	getLocalTimeZone,
	parseDateTime as parseInternationalizedDateTime,
	Time,
	toCalendarDateTime,
	toZoned,
} from "@internationalized/date";
import { isValidTimezone } from "@sitescene/shared";

/**
 * Format a date to a time string (HH:MM)
 * @param date - Date object or date string
 * @param options - Formatting options
 * @returns Formatted time string (e.g., "14:30" or "02:30 PM")
 */
export const formatTime = (
	date: Date | number | string,
	options?: {
		hour12?: boolean;
		includeSeconds?: boolean;
		locale?: string;
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const {
		hour12 = true,
		includeSeconds = false,
		locale = "en-US",
	} = options || {};

	return dateObj.toLocaleTimeString(locale, {
		hour: "numeric",
		hour12,
		minute: "2-digit",
		second: includeSeconds ? "2-digit" : undefined,
	});
};

/**
 * Format a date to a date string (YYYY-MM-DD)
 * @param date - Date object or date string
 * @returns Formatted date string (e.g., "2025-01-15")
 */
export const formatDate = (date: Date | number | string): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	return dateObj.toISOString().split("T")[0]!;
};

/**
 * Format a date to MM/DD/YYYY format
 * @param date - Date object or date string
 * @returns Formatted date string (e.g., "12/14/2025")
 */
export const formatDateShort = (date: Date | number | string): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	return dateObj.toLocaleDateString("en-US", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

/**
 * Format a date to a readable date string
 * @param date - Date object or date string
 * @param options - Formatting options
 * @returns Formatted date string (e.g., "Jan 15, 2025" or "January 15, 2025")
 */
export const formatDateReadable = (
	date: Date | number | string,
	options?: {
		day?: "2-digit" | "numeric";
		locale?: string;
		month?: "long" | "numeric" | "short";
		year?: "2-digit" | "numeric";
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const {
		day = "numeric",
		locale = "en-US",
		month = "short",
		year = "numeric",
	} = options || {};

	return dateObj.toLocaleDateString(locale, {
		day,
		month,
		year,
	});
};

/**
 * Format a date and time together
 * @param date - Date object or date string
 * @param options - Formatting options
 * @returns Formatted date-time string (e.g., "Jan 15, 2025 at 2:30 PM")
 */
export const formatDateTime = (
	date: Date | number | string,
	options?: {
		dateFormat?: "long" | "numeric" | "short";
		includeSeconds?: boolean;
		locale?: string;
		timeFormat?: "12h" | "24h";
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const {
		dateFormat = "short",
		includeSeconds = false,
		locale = "en-US",
		timeFormat = "24h",
	} = options || {};

	const dateStr = formatDateReadable(dateObj, {
		locale,
		month: dateFormat === "long" ? "long" : "short",
	});
	const timeStr = formatTime(dateObj, {
		hour12: timeFormat === "12h",
		includeSeconds,
		locale,
	});

	return `${dateStr} at ${timeStr}`;
};

/**
 * Convert a date to ISO string
 * @param date - Date object or date string
 * @returns ISO string (e.g., "2025-01-15T14:30:00.000Z")
 */
export const toISOString = (date: Date | number | string): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	return dateObj.toISOString();
};

/**
 * Convert a date to Unix timestamp (seconds)
 * @param date - Date object or date string
 * @returns Unix timestamp in seconds
 */
export const toUnixTimestamp = (date: Date | number | string): number => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	return Math.floor(dateObj.getTime() / 1000);
};

/**
 * Convert a Unix timestamp to Date object
 * @param timestamp - Unix timestamp in seconds
 * @returns Date object
 */
export const fromUnixTimestamp = (timestamp: number): Date => {
	return new Date(timestamp * 1000);
};

/**
 * Convert a date string to Date object
 * @param dateString - Date string in various formats
 * @returns Date object or null if invalid
 */
export const parseDate = (dateString: string): Date | null => {
	const date = new Date(dateString);
	return isNaN(date.getTime()) ? null : date;
};

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * @param date - Date object or date string
 * @param options - Formatting options
 * @returns Relative time string
 */
export const formatRelativeTime = (
	date: Date | number | string,
	options?: {
		locale?: string;
		numeric?: "always" | "auto";
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const now = new Date();
	const diffMs = dateObj.getTime() - now.getTime();
	const { locale = "en-US", numeric = "auto" } = options || {};

	const rtf = new Intl.RelativeTimeFormat(locale, { numeric });
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);
	const diffWeeks = Math.floor(diffDays / 7);
	const diffMonths = Math.floor(diffDays / 30);
	const diffYears = Math.floor(diffDays / 365);

	if (Math.abs(diffYears) > 0) {
		return rtf.format(diffYears, "year");
	}
	if (Math.abs(diffMonths) > 0) {
		return rtf.format(diffMonths, "month");
	}
	if (Math.abs(diffWeeks) > 0) {
		return rtf.format(diffWeeks, "week");
	}
	if (Math.abs(diffDays) > 0) {
		return rtf.format(diffDays, "day");
	}
	if (Math.abs(diffHours) > 0) {
		return rtf.format(diffHours, "hour");
	}
	if (Math.abs(diffMinutes) > 0) {
		return rtf.format(diffMinutes, "minute");
	}
	return rtf.format(diffSeconds, "second");
};

/**
 * Format elapsed time between two instants as a full day/hour/minute/second
 * breakdown. `end` defaults to now — use that for “created → viewed” (or any
 * capture that happens at the moment of the action).
 *
 * @param start - Earlier instant (e.g. event `createdAt`)
 * @param end - Later instant (e.g. when the operator viewed the event)
 * @returns e.g. `"Days: 1 Hours: 2 Minutes: 15 Seconds: 30"`
 */
export const formatElapsedBreakdown = (
	start: Date | number | string,
	end: Date | number | string = Date.now(),
): string => {
	const startMs =
		typeof start === "string" || typeof start === "number"
			? new Date(start).getTime()
			: start.getTime();
	const endMs =
		typeof end === "string" || typeof end === "number"
			? new Date(end).getTime()
			: end.getTime();

	if (Number.isNaN(startMs) || Number.isNaN(endMs)) return "";

	const totalSeconds = Math.max(0, Math.round((endMs - startMs) / 1000));
	const days = Math.floor(totalSeconds / 86_400);
	const hours = Math.floor((totalSeconds % 86_400) / 3_600);
	const minutes = Math.floor((totalSeconds % 3_600) / 60);
	const seconds = totalSeconds % 60;

	return `Days: ${days} Hours: ${hours} Minutes: ${minutes} Seconds: ${seconds}`;
};

/**
 * Get the start of a day
 * @param date - Date object or date string
 * @returns Date object set to 00:00:00.000
 */
export const startOfDay = (date: Date | number | string): Date => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const result = new Date(dateObj);
	result.setHours(0, 0, 0, 0);
	return result;
};

/**
 * Get the end of a day
 * @param date - Date object or date string
 * @returns Date object set to 23:59:59.999
 */
export const endOfDay = (date: Date | number | string): Date => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const result = new Date(dateObj);
	result.setHours(23, 59, 59, 999);
	return result;
};

/**
 * Add time to a date
 * @param date - Date object or date string
 * @param amount - Amount to add
 * @param unit - Unit of time
 * @returns New Date object
 */
export const addTime = (
	date: Date | number | string,
	amount: number,
	unit: "days" | "hours" | "minutes" | "months" | "seconds" | "weeks" | "years",
): Date => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const result = new Date(dateObj);

	switch (unit) {
		case "days":
			result.setDate(result.getDate() + amount);
			break;
		case "hours":
			result.setHours(result.getHours() + amount);
			break;
		case "minutes":
			result.setMinutes(result.getMinutes() + amount);
			break;
		case "months":
			result.setMonth(result.getMonth() + amount);
			break;
		case "seconds":
			result.setSeconds(result.getSeconds() + amount);
			break;
		case "weeks":
			result.setDate(result.getDate() + amount * 7);
			break;
		case "years":
			result.setFullYear(result.getFullYear() + amount);
			break;
	}

	return result;
};

/**
 * Subtract time from a date
 * @param date - Date object or date string
 * @param amount - Amount to subtract
 * @param unit - Unit of time
 * @returns New Date object
 */
export const subtractTime = (
	date: Date | number | string,
	amount: number,
	unit: "days" | "hours" | "minutes" | "months" | "seconds" | "weeks" | "years",
): Date => {
	return addTime(date, -amount, unit);
};

/**
 * Get the difference between two dates
 * @param date1 - First date
 * @param date2 - Second date (defaults to now)
 * @param unit - Unit to return difference in
 * @returns Difference in the specified unit
 */
export const getTimeDifference = (
	date1: Date | number | string,
	date2: Date | number | string = new Date(),
	unit:
		| "days"
		| "hours"
		| "minutes"
		| "months"
		| "seconds"
		| "weeks"
		| "years" = "days",
): number => {
	const dateObj1 =
		typeof date1 === "string" || typeof date1 === "number"
			? new Date(date1)
			: date1;
	const dateObj2 =
		typeof date2 === "string" || typeof date2 === "number"
			? new Date(date2)
			: date2;
	const diffMs = dateObj1.getTime() - dateObj2.getTime();

	switch (unit) {
		case "days":
			return Math.floor(diffMs / (1000 * 60 * 60 * 24));
		case "hours":
			return Math.floor(diffMs / (1000 * 60 * 60));
		case "minutes":
			return Math.floor(diffMs / (1000 * 60));
		case "months":
			return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));
		case "seconds":
			return Math.floor(diffMs / 1000);
		case "weeks":
			return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
		case "years":
			return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365));
	}
};

/**
 * Check if a date is today
 * @param date - Date object or date string
 * @returns True if the date is today
 */
export const isToday = (date: Date | number | string): boolean => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const today = new Date();
	return formatDate(dateObj) === formatDate(today);
};

/**
 * Check if a date is yesterday
 * @param date - Date object or date string
 * @returns True if the date is yesterday
 */
export const isYesterday = (date: Date | number | string): boolean => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const yesterday = subtractTime(new Date(), 1, "days");
	return formatDate(dateObj) === formatDate(yesterday);
};

/**
 * Check if a date is tomorrow
 * @param date - Date object or date string
 * @returns True if the date is tomorrow
 */
export const isTomorrow = (date: Date | number | string): boolean => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const tomorrow = addTime(new Date(), 1, "days");
	return formatDate(dateObj) === formatDate(tomorrow);
};

/**
 * Check if a date is in the past
 * @param date - Date object or date string
 * @returns True if the date is in the past
 */
export const isPast = (date: Date | number | string): boolean => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	return dateObj.getTime() < new Date().getTime();
};

/**
 * Check if a date is in the future
 * @param date - Date object or date string
 * @returns True if the date is in the future
 */
export const isFuture = (date: Date | number | string): boolean => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	return dateObj.getTime() > new Date().getTime();
};

/**
 * Convert a date to a different timezone
 * @param date - Date object or date string
 * @param timeZone - Target timezone (e.g., "America/New_York", "Europe/London")
 * @returns Formatted date string in the target timezone
 */
export const convertToTimeZone = (
	date: Date | number | string,
	timeZone: string,
	options?: {
		dateStyle?: "full" | "long" | "medium" | "short";
		locale?: string;
		timeStyle?: "full" | "long" | "medium" | "short";
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const {
		dateStyle = "short",
		locale = "en-US",
		timeStyle = "short",
	} = options || {};

	const formatOptions: Intl.DateTimeFormatOptions = {
		dateStyle,
		timeStyle,
	};

	if (isValidTimezone(timeZone)) {
		formatOptions.timeZone = timeZone;
	}

	return new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);
};

/**
 * Get the day of the week name
 * @param date - Date object or date string
 * @param options - Formatting options
 * @returns Day name (e.g., "Monday" or "Mon")
 */
export const getDayName = (
	date: Date | number | string,
	options?: {
		format?: "long" | "narrow" | "short";
		locale?: string;
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const { format = "long", locale = "en-US" } = options || {};

	return dateObj.toLocaleDateString(locale, { weekday: format });
};

/**
 * Get the month name
 * @param date - Date object or date string
 * @param options - Formatting options
 * @returns Month name (e.g., "January" or "Jan")
 */
export const getMonthName = (
	date: Date | number | string,
	options?: {
		format?: "long" | "narrow" | "short";
		locale?: string;
	},
): string => {
	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const { format = "long", locale = "en-US" } = options || {};

	return dateObj.toLocaleDateString(locale, { month: format });
};

/**
 * Combine date and time strings into a Date object
 * @param dateString - Date string (YYYY-MM-DD)
 * @param timeString - Time string (HH:MM or HH:MM:SS)
 * @returns Date object
 */
export const combineDateAndTime = (
	dateString: string,
	timeString: string,
): Date => {
	const [hours, minutes, seconds] = timeString.split(":").map(Number);
	const date = new Date(dateString);
	date.setHours(hours || 0, minutes || 0, seconds || 0, 0);
	return date;
};

/**
 * Format duration in milliseconds to a human-readable string
 * @param milliseconds - Duration in milliseconds
 * @param options - Formatting options
 * @returns Formatted duration string (e.g., "2h 30m" or "1d 5h 20m")
 */
export const formatDuration = (
	milliseconds: number,
	options?: {
		format?: "long" | "short";
		locale?: string;
	},
): string => {
	const { format = "short", locale = "en-US" } = options || {};
	const seconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	// Use Intl.RelativeTimeFormat for locale-aware formatting
	const rtf = new Intl.RelativeTimeFormat(locale, {
		numeric: "always",
		style: format === "short" ? "narrow" : "long",
	});

	const parts: string[] = [];

	if (days > 0) {
		const formatted = rtf.format(days, "day");
		// Remove "in " prefix if present (Intl.RelativeTimeFormat may add it)
		parts.push(formatted.replace(/^in\s+/i, ""));
	}
	if (hours % 24 > 0) {
		const formatted = rtf.format(hours % 24, "hour");
		parts.push(formatted.replace(/^in\s+/i, ""));
	}
	if (minutes % 60 > 0 && days === 0) {
		const formatted = rtf.format(minutes % 60, "minute");
		parts.push(formatted.replace(/^in\s+/i, ""));
	}
	if (seconds % 60 > 0 && hours === 0 && days === 0) {
		const formatted = rtf.format(seconds % 60, "second");
		parts.push(formatted.replace(/^in\s+/i, ""));
	}

	if (parts.length === 0) {
		const formatted = rtf.format(0, "second");
		return formatted.replace(/^in\s+/i, "");
	}

	return parts.join(" ");
};

/**
 * Format a timezone to a human-readable string
 * @param timezone - Timezone (e.g., "America/New_York", "Europe/London")
 * @returns Formatted timezone string (e.g., "EST", "PST"), or undefined if invalid
 */
export const formatTimezone = (timezone: string): string | undefined => {
	try {
		const formatter = new Intl.DateTimeFormat("en-US", {
			timeZone: timezone,
			timeZoneName: "short",
		});
		const parts = formatter.formatToParts(new Date());
		const timeZonePart = parts.find((part) => part.type === "timeZoneName");
		return timeZonePart?.value;
	} catch {
		return undefined;
	}
};

/**
 * Get the IANA timezone identifier from a populated installation.
 * Returns the raw IANA identifier (e.g., "America/New_York") safe for use
 * as a `timeZone` option in Intl APIs.
 * Returns undefined if installation/location is not populated or timezone is invalid.
 */
export const getInstallationTimezone = (
	installation: InstallationDto | string,
): string | undefined => {
	if (typeof installation === "string") {
		return undefined;
	}
	const location = installation.location;
	if (typeof location === "string" || !location) {
		return undefined;
	}
	if (!location.timezone || !isValidTimezone(location.timezone)) {
		return undefined;
	}
	return location.timezone;
};

/**
 * Format a time with timezone abbreviation from an installation.
 * Safely handles unresolved installation IDs, missing locations,
 * and invalid timezone identifiers by falling back to local time with no timezone label.
 *
 * @param date - Date object, date string, or timestamp
 * @param installation - Installation object or string ref (or undefined)
 * @param options - Formatting options for the time portion
 * @returns Formatted string, e.g. "2:30 PM EST" or "2:30 PM" if timezone unavailable
 */
export const formatTimeWithTimezone = (
	date: Date | number | string,
	installation?: InstallationDto | string,
	options?: {
		hour12?: boolean;
		includeSeconds?: boolean;
		locale?: string;
	},
): string => {
	if (!installation) {
		return formatTime(date, options);
	}
	const tz = getInstallationTimezone(installation);
	if (!tz) {
		return formatTime(date, options);
	}

	const dateObj =
		typeof date === "string" || typeof date === "number"
			? new Date(date)
			: date;
	const {
		hour12 = true,
		includeSeconds = false,
		locale = "en-US",
	} = options || {};

	return dateObj.toLocaleTimeString(locale, {
		hour: "numeric",
		hour12,
		minute: "2-digit",
		second: includeSeconds ? "2-digit" : undefined,
		timeZone: tz,
		timeZoneName: "short",
	});
};

/**
 * Parses a calendar date or datetime string into a React Aria `CalendarDateTime`.
 * Accepts `YYYY-MM-DD` (→ `T00:00:00`) and `YYYY-MM-DDTHH:mm[:ss]`.
 * Returns null for empty or invalid values.
 *
 * Used by: round-trip serialization tests. Prefer `parseCalendarDateRangeBound`
 * for filter URL and localStorage hydration bounds.
 */
export const parseCalendarDateString = (
	value: null | string | undefined,
): DateValue | null => {
	if (!value) return null;
	try {
		return parseInternationalizedDateTime(value);
	} catch {
		return null;
	}
};

const CALENDAR_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a filter URL datetime bound. Date-only strings (`YYYY-MM-DD`) become
 * start `00:00` or end `23:59` so the full day is included. Strings with an
 * explicit time are parsed as-is.
 */
export const parseCalendarDateRangeBound = (
	value: null | string | undefined,
	role: "end" | "start",
): DateValue | null => {
	if (!value) return null;
	const parsed = parseCalendarDateString(value);
	if (!parsed) return null;
	if (CALENDAR_DATE_ONLY_PATTERN.test(value.trim())) {
		return role === "start"
			? toDayStartDateTime(parsed)
			: toDayEndDateTime(parsed);
	}
	return parsed;
};

/**
 * Serializes a React Aria date/datetime for URL params and localStorage.
 * Timezone-neutral — the same string round-trips through `parseCalendarDateString`.
 * `CalendarDateTime` → `YYYY-MM-DDTHH:mm:ss`; date-only values stringify as `YYYY-MM-DD`.
 *
 * Used by: `filter-url-params`, `stored-events-filters`.
 */
export const dateValueToCalendarDateString = (date: DateValue): string =>
	date.toString();

/** Start-of-day `CalendarDateTime` (00:00) for the given calendar date parts. */
export const toDayStartDateTime = (date: DateValue): DateValue =>
	toCalendarDateTime(date, new Time(0, 0));

/** End-of-day `CalendarDateTime` (23:59) for the given calendar date parts. */
export const toDayEndDateTime = (date: DateValue): DateValue =>
	toCalendarDateTime(date, new Time(23, 59));

/**
 * Wall-clock hour/minute for a range bound, defaulting date-only values to the
 * full-day boundary (start → 00:00, end → 23:59). Single source of truth for the
 * full-day convention shared by the query builder, matcher, and picker.
 */
const wallClockHm = (
	date: DateValue,
	role: "end" | "start",
): { hour: number; minute: number } => {
	if (!("hour" in date)) {
		return role === "start" ? { hour: 0, minute: 0 } : { hour: 23, minute: 59 };
	}
	return { hour: date.hour, minute: date.minute };
};

/**
 * True when the range carries a non–full-day wall clock (anything other than
 * start 00:00 and end 23:59). Used for UI hints (e.g. timezone warning) when
 * the user has set explicit times.
 */
export const dateRangeHasExplicitTime = (
	dateRange: null | { end: DateValue; start: DateValue },
): boolean => {
	if (!dateRange) return false;
	const { hour: startHour, minute: startMinute } = wallClockHm(
		dateRange.start,
		"start",
	);
	const { hour: endHour, minute: endMinute } = wallClockHm(
		dateRange.end,
		"end",
	);
	return !(
		startHour === 0 &&
		startMinute === 0 &&
		endHour === 23 &&
		endMinute === 59
	);
};

/**
 * Normalize a picker range to `CalendarDateTime`. When both sides are midnight
 * (typical calendar-only selection), set the end to 23:59 so a date range still
 * means full days unless the user edits times.
 */
export const normalizeDateTimeRange = (
	dateRange: null | { end: DateValue; start: DateValue },
): null | { end: DateValue; start: DateValue } => {
	if (!dateRange) return null;

	const start = toCalendarDateTime(dateRange.start);
	let end = toCalendarDateTime(dateRange.end);

	if (
		start.hour === 0 &&
		start.minute === 0 &&
		end.hour === 0 &&
		end.minute === 0
	) {
		end = end.set({ hour: 23, minute: 59 });
	}

	return { end, start };
};

/**
 * Apply calendar date parts onto an existing draft, preserving wall-clock times
 * when the user already chose non–full-day times. Used by the date range picker
 * when the calendar changes while a draft with explicit times is open.
 */
export const mergeCalendarDatesIntoDraft = (
	range: { end: DateValue; start: DateValue },
	draft: null | { end: DateValue; start: DateValue },
): null | { end: DateValue; start: DateValue } => {
	if (draft && dateRangeHasExplicitTime(draft)) {
		const { hour: startHour, minute: startMinute } = wallClockHm(
			draft.start,
			"start",
		);
		const { hour: endHour, minute: endMinute } = wallClockHm(draft.end, "end");
		return {
			end: toCalendarDateTime(range.end, new Time(endHour, endMinute)),
			start: toCalendarDateTime(range.start, new Time(startHour, startMinute)),
		};
	}
	return normalizeDateTimeRange(range);
};

/**
 * Milliseconds added to the upper bound so an event landing within the end
 * wall-clock minute is included (23:59 + buffer stays on the end day). Shared
 * by the API query builder and the websocket matcher so both agree on the
 * boundary.
 */
export const DATE_TIME_RANGE_END_BUFFER_MS = 59_999;

/** True when end is before or equal to start (inverted or zero-length span). */
export const isDateRangeInverted = (
	dateRange: null | { end: DateValue; start: DateValue },
): boolean => getDateRangeValidationMessage(dateRange) !== null;

/** User-facing validation message for an invalid date/datetime range, if any. */
export const getDateRangeValidationMessage = (
	dateRange: null | { end: DateValue; start: DateValue },
): null | string => {
	if (!dateRange?.start || !dateRange.end) {
		return null;
	}

	const comparison = dateRange.end.compare(dateRange.start);
	if (comparison < 0) {
		return "End date and time must be after the start.";
	}
	if (comparison === 0) {
		return "Start and end cannot be the same date and time.";
	}
	return null;
};

/** Anchor a wall-clock date + numeric hour/minute to an IANA timezone → ISO instant. */
const wallClockToApiTimestamp = (
	date: DateValue,
	hour: number,
	minute: number,
	timeZone: string,
): string =>
	toZoned(toCalendarDateTime(date, new Time(hour, minute)), timeZone)
		.toDate()
		.toISOString();

/**
 * Combine a calendar date + `HH:mm` wall-clock, interpreted in `timeZone`,
 * into an ISO instant. Unlike browser-local `Date.toDate()`, this anchors the
 * wall-clock to the location's timezone so bounds match the times shown in the
 * events table.
 */
export const dateTimeToApiTimestamp = (
	date: DateValue,
	time: string, // "HH:mm"
	timeZone: string,
): string => {
	const [hour, minute] = time.split(":").map(Number);
	return wallClockToApiTimestamp(date, hour || 0, minute || 0, timeZone);
};

/**
 * Build the single continuous datetime span (as ISO `startTimestamp` /
 * `endTimestamp`) from a date/datetime range.
 *
 * When `timeZone` is set, wall clocks are anchored to that IANA timezone
 * (location display). When null/undefined, the browser local timezone is used —
 * same path for the events query and the WebSocket matcher. Hours/minutes come
 * from the `DateValue`s (defaults: start `00:00`, end `23:59` for date-only
 * values). The end bound gets `DATE_TIME_RANGE_END_BUFFER_MS` added.
 *
 * Returns null only when there is no date range.
 */
export const buildDateTimeRangeTimestamps = (
	dateRangeFilter: null | { end: DateValue; start: DateValue },
	timeZone?: null | string,
): null | { endTimestamp: string; startTimestamp: string } => {
	if (!dateRangeFilter) {
		return null;
	}

	const resolvedTimeZone = timeZone || getLocalTimeZone();
	const startHm = wallClockHm(dateRangeFilter.start, "start");
	const endHm = wallClockHm(dateRangeFilter.end, "end");

	const startTimestamp = wallClockToApiTimestamp(
		dateRangeFilter.start,
		startHm.hour,
		startHm.minute,
		resolvedTimeZone,
	);
	const endBase = new Date(
		wallClockToApiTimestamp(
			dateRangeFilter.end,
			endHm.hour,
			endHm.minute,
			resolvedTimeZone,
		),
	);
	const endTimestamp = new Date(
		endBase.getTime() + DATE_TIME_RANGE_END_BUFFER_MS,
	).toISOString();

	return { endTimestamp, startTimestamp };
};

/** Compact display for active watch countdown (e.g. `1h 05m`, `12m`). */
export function formatTimeRemaining(milliseconds: number): string {
	const totalMinutes = Math.max(0, Math.floor(milliseconds / 60_000));
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	if (hours > 0) {
		return `${hours}h ${String(minutes).padStart(2, "0")}m`;
	}

	return `${minutes}m`;
}
